import {NestableActionMenu} from '@github-ui/nestable-action-menu'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {IconButton, merge, type IconButtonProps} from '@primer/react'
import {Fragment, useMemo} from 'react'
import {useActionBarContent} from './ActionBarContentContext'
import {useActionBarResize} from './ActionBarResizeContext'
import {testIdProps} from '@github-ui/test-id-props'
import {useActionBarRef} from './ActionBarRefContext'

export type OverflowMenuProps = {
  anchorProps?: Omit<IconButtonProps, 'aria-labelledby'>
}

const defaultMenuToggleIcon = KebabHorizontalIcon
const defaultMenuToggleVariant = 'invisible'

export const OverflowMenu = ({
  anchorProps: {
    'aria-label': ariaLabel,
    icon = defaultMenuToggleIcon,
    variant = defaultMenuToggleVariant,
    ...otherAnchorProps
  } = {
    icon: defaultMenuToggleIcon,
    variant: defaultMenuToggleVariant,
  },
}: OverflowMenuProps) => {
  const {actions, staticMenuActions, label} = useActionBarContent()
  const {visibleChildEndIndex} = useActionBarResize()
  const {anchorRef} = useActionBarRef()
  const anchorProps = useMemo(
    () =>
      merge(otherAnchorProps, {
        'aria-label': ariaLabel?.trim() || `More ${label}`,
        icon,
        sx: {flexShrink: 0},
        variant,
      }),
    [ariaLabel, icon, label, variant, otherAnchorProps],
  )
  const overflowActions = useMemo(() => actions?.slice(visibleChildEndIndex), [actions, visibleChildEndIndex])

  const hasOverflowActions = overflowActions && overflowActions.length > 0
  const hasStaticMenuActions = staticMenuActions && staticMenuActions.length > 0

  if (!hasOverflowActions && !hasStaticMenuActions) return null

  return (
    <NestableActionMenu
      anchor={<IconButton {...testIdProps('overflow-menu-anchor')} {...anchorProps} />}
      overlayProps={{align: 'end'}}
      anchorRef={anchorRef}
    >
      <>
        {hasStaticMenuActions &&
          staticMenuActions.map(staticMenuAction => (
            <Fragment key={`${staticMenuAction.key}`}>{staticMenuAction.render()}</Fragment>
          ))}

        {overflowActions?.map(action => <Fragment key={action.key}>{action.render(true)}</Fragment>)}
      </>
    </NestableActionMenu>
  )
}

try{ OverflowMenu.displayName ||= 'OverflowMenu' } catch {}